import type { UserMemory } from './memory.ts';

/**
 * The tab catalogue. The model can't invent UI: it can only open a tab type defined here, and only once the tab has
 * something to show (its prerequisite). A user's tabs are `memory.tabs`, in the order they were opened.
 */
export const TAB_TYPES = ['progress', 'interview'] as const;
export type TabType = (typeof TAB_TYPES)[number];

type TabSpec = {
  title: string;
  /** What it shows, and what it needs before it can open, in words for the model (open_tab's description). */
  about: string;
  needs: string;
  /** Why the tab can't open yet, or null when it can. */
  blocked(memory: UserMemory): string | null;
  /** Reason recorded when post-call rules open it. */
  autoReason: string;
};

export const TABS: Record<TabType, TabSpec> = {
  progress: {
    title: 'Progress',
    about: 'speaking level, sounds, grammar and pace over time',
    needs: 'at least one scored answer',
    blocked: (m) => (m.skills.samples > 0 ? null : 'no scored answers yet'),
    autoReason: 'Tracks your speaking level and the sounds, grammar and pace to work on.',
  },
  interview: {
    title: 'Interview',
    about: 'their target interview and every question practised',
    needs: 'a target role',
    blocked: (m) => (m.fields['interview.role']?.value ? null : 'no target role yet'),
    autoReason: 'Your interview at a glance, with every question you have practised.',
  },
};

/** "a and b", "a, b and c": how the prompt and open_tab's description list the tabs. */
export function joinList(items: readonly string[], word: 'and' | 'or'): string {
  return items.length < 3 ? items.join(` ${word} `) : `${items.slice(0, -1).join(', ')} ${word} ${items.at(-1)}`;
}

export function isTabType(t: string): t is TabType {
  return (TAB_TYPES as readonly string[]).includes(t);
}

export function hasTab(memory: UserMemory, type: TabType): boolean {
  return memory.tabs.some((t) => t.type === type);
}

/** Opens a tab if its prerequisite holds and it isn't open yet. Pure; returns the same memory when nothing changes. */
export function openTab(
  memory: UserMemory,
  type: TabType,
  reason: string,
  at = new Date().toISOString(),
): { memory: UserMemory; opened: boolean; why?: string } {
  if (hasTab(memory, type)) return { memory, opened: false, why: 'already open' };
  const blocked = TABS[type].blocked(memory);
  if (blocked) return { memory, opened: false, why: blocked };
  return {
    memory: { ...memory, tabs: [...memory.tabs, { type, openedAt: at, reason: reason.slice(0, 200) }] },
    opened: true,
  };
}

/** After the assessment call: every tab that has something to show appears (the coach may have opened some already). */
export function openTabsAfterAssessment(memory: UserMemory): UserMemory {
  if (memory.journey.stage !== 'assessed') return memory;
  return TAB_TYPES.reduce((m, type) => openTab(m, type, TABS[type].autoReason).memory, memory);
}
